import axios, { AxiosError } from 'axios'
import { getStoredUser } from '@modules/auth/authStore'

export const apiBase =
  (import.meta as any).env?.VITE_API_BASE?.replace(/\/$/, '') || window.location.origin

export const api = axios.create({
  baseURL: apiBase,
  timeout: 20000,
  withCredentials: false,
  headers: { 'X-Requested-With': 'XMLHttpRequest' },
})

api.interceptors.request.use((cfg) => {
  const token = getStoredUser()?.token
  if (token) {
    cfg.headers = cfg.headers ?? {}
    cfg.headers.Authorization = `Bearer ${token}`
  }
  return cfg
})

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<any>) => {
    const data = err.response?.data as any
    const detail =
      data?.detail ??
      data?.message ??
      (typeof data === 'string' ? data : undefined) ??
      err.message
    const copy: any = new Error(detail || 'Ukjent feil')
    copy.response = err.response
    copy.code = err.code
    return Promise.reject(copy)
  },
)

export async function login(identifier: string, password: string): Promise<string> {
  const form = new URLSearchParams()
  form.set('grant_type', 'password')
  form.set('username', identifier)
  form.set('password', password)
  const { data } = await api.post('/auth/token', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  })
  return (data as any).access_token || (data as any).token
}

export type CustomerContext = {
  customer_id: number
  customer_name: string
  location_tag: string
  role: string
  permissions: string[]
  can_access_vakt: boolean
  selected: boolean
}

export type CustomerContexts = {
  contexts: CustomerContext[]
  selected_customer_id: number | null
}

export async function getCustomerContexts(): Promise<CustomerContexts> {
  const { data } = await api.get('/auth/customer-contexts')
  return data as CustomerContexts
}

export async function selectCustomerContext(customerId: number): Promise<string> {
  const { data } = await api.post(`/auth/customer-contexts/${customerId}/token`)
  return (data as { access_token: string }).access_token
}

export type VaktShiftClaim = {
  customer_id: number
  shift_instance_id: number
  shift_key: string
  shift_label: string
  claim_id: number
  checklist_url: string
  claimed_at_utc: string
}

export type VaktScanShiftOption = {
  customer_id: number
  shift_instance_id: number
  shift_key: string
  shift_label: string
  local_date: string
  start_at_utc: string
  end_at_utc: string
  checklist_count: number
  task_count: number
}

export async function getVaktScanSessionShifts(sessionToken: string): Promise<VaktScanShiftOption[]> {
  const { data } = await api.get(`/api/vakt/scan-sessions/${encodeURIComponent(sessionToken)}/shifts`)
  return (data as { shifts: VaktScanShiftOption[] }).shifts
}

export async function claimVaktScanSessionShift(
  sessionToken: string,
  shiftInstanceId: number,
): Promise<VaktShiftClaim> {
  const { data } = await api.post(
    `/api/vakt/scan-sessions/${encodeURIComponent(sessionToken)}/claims`,
    { shift_instance_id: shiftInstanceId },
  )
  return data as VaktShiftClaim
}
